import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Building2, TruckIcon, Shield, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BG_IMAGE = "https://images.unsplash.com/photo-1766785368863-f2188a8c8b32?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200";

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Redirect to dashboard once authenticated (avoid navigate-during-render)
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, user, navigate]);

  if (loading) return null;

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Email and password are required'); return; }
    setError('');
    setSubmitting(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      const { user: userData, session_token } = res.data;
      if (session_token) localStorage.setItem('bm_session_token', session_token);
      setUser(userData);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: Image Panel */}
      <div
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative bg-slate-900"
        style={{ backgroundImage: `url(${BG_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <TruckIcon size={22} className="text-white" />
              </div>
              <span className="text-white font-bold text-xl font-['Manrope']">BillMaster Cloud</span>
            </div>
            <h1 className="text-4xl font-bold text-white font-['Manrope'] leading-tight mb-3">
              Logistics ERP<br />Built for Speed
            </h1>
            <p className="text-slate-300 text-base max-w-md">
              Manage Delivery Orders, track shipments, and generate PDFs instantly with our intelligent 3-tier access control system.
            </p>
          </div>
          <div className="flex gap-6">
            {[
              { label: 'DO Generation', desc: 'Automated numbering' },
              { label: 'PDF Engine', desc: 'Instant generation' },
              { label: 'RBAC', desc: '3-tier access control' },
            ].map(f => (
              <div key={f.label} className="flex-1">
                <div className="text-blue-400 font-semibold text-sm">{f.label}</div>
                <div className="text-slate-400 text-xs">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Login Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center">
              <Building2 size={18} className="text-white" />
            </div>
            <span className="font-bold text-slate-800 font-['Manrope']">BillMaster Cloud</span>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 font-['Manrope'] mb-1">Welcome back</h2>
            <p className="text-slate-500 text-sm">Sign in to access your ERP dashboard</p>
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="email-login-form">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700" data-testid="login-error">
                {error}
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Email</Label>
              <Input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="text-sm w-full"
                data-testid="email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="text-sm w-full pr-10"
                  data-testid="password-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="email-login-btn"
            >
              {submitting ? 'Signing in...' : 'Sign In'}
            </Button>
            <div className="text-center mt-1">
              <Link to="/forgot-password"
                className="text-xs text-slate-400 hover:text-blue-600 transition-colors"
                data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </form>

          <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-start gap-3">
              <Shield size={16} className="text-blue-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-slate-700 text-xs font-semibold mb-1">Role-Based Access</p>
                <p className="text-slate-500 text-xs">Your access level (Super Admin, Admin, or Broker) is assigned by your system administrator.</p>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            BillMaster Cloud ERP &copy; {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}
